import { type IdTokenClaims } from '@logto/vue'
import { defineStore } from 'pinia'
import posthog from 'posthog-js'
import { computed, ref } from 'vue'
import { getPusher } from '@/plugins/pusherPlugin'
import { Role, RoleGroup, RoleGroupMapping } from '@/types/roles'
import { useAuth } from '../composables/src/useAuth'
import { getUser as getUserService } from '../service/users'

export const useAccountStore = defineStore('account', () => {
  const user = ref<Partial<IdTokenClaims>>({})

  const getUser = async () => {
    return Promise.resolve(user.value)
  }

  const setUser = async () => {
    const { fetchActualUser } = useAuth()
    const userId = await fetchActualUser()
    const { onSuccess } = getUserService(userId.sub)

    return new Promise<void>((resolve) => {
      onSuccess((data) => {
        user.value = data
        posthog.identify(data.id, { email: data.primaryEmail, name: data.name, role: data.role })

        const pusher = getPusher()

        pusher.signin()
        resolve()
      })
    })
  }

  const updateUser = async (updatedUser: Partial<IdTokenClaims>) => {
    user.value = updatedUser
  }

  const onUserLogout = () => {
    user.value = {}
  }

  const isClientUser = computed(() => {
    return user.value.role === Role.Client
  })
  const userRole = computed(() => {
    return user.value?.role
  })

  const isCustomerUser = computed(() => {
    return RoleGroupMapping[user.value.role as (typeof Role)[keyof typeof Role]] === RoleGroup.Customer
  })

  const isAgentUser = computed(() => {
    return RoleGroupMapping[user.value.role as (typeof Role)[keyof typeof Role]] === RoleGroup.Agent
  })

  const isClientManager = computed(() => {
    return user.value.role === Role.ClientManager
  })

  const isClientApprover = computed(() => {
    return user.value.role === Role.ClientApprover
  })

  const isAdminUser = computed(() => {
    return user.value.role === Role.Admin
  })

  const togglToken = computed(() => {
    return (user.value as any)?.customData?.togglToken || null
  })

  return {
    isAdminUser,
    isAgentUser,
    isClientUser,
    isCustomerUser,
    isClientManager,
    isClientApprover,
    user,
    togglToken,
    getUser,
    setUser,
    updateUser,
    onUserLogout,
    userRole,
  }
})
